import React, {
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import ArrowRight from '@material-ui/icons/ArrowRight';

const styles = theme => ({
  root: {
    background: theme.palette.common.white,
  },
  menuItem: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  menuItemLeftContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignSelf: 'flex-start',
    alignItems: 'center',
  },
  menuItemLeftIcon: {
    marginRight: theme.spacing(1),
  },
});

const NestedMenuItem = forwardRef(
  ({
    classes,
    className,
    parentMenuOpen,
    label,
    leftIcon = null,
    rightIcon = <ArrowRight />,
    children,
    ContainerProps: ContainerPropsProp = {},
    MenuItemProps,
  }, ref) => {
    const [isSubMenuOpen, setIsSubMenuOpen] = useState(false);
    const open = isSubMenuOpen && parentMenuOpen;

    const { ref: containerRefProp, ...ContainerProps } = ContainerPropsProp;

    const menuItemRef = useRef(null);
    useImperativeHandle(ref, () => menuItemRef.current);

    const containerRef = useRef(null);
    useImperativeHandle(containerRefProp, () => containerRef.current);

    const menuContainerRef = useRef(null);

    const handleMouseEnter = () => {
      setIsSubMenuOpen(true);
    };

    const handleMouseLeave = () => {
      setIsSubMenuOpen(false);
    };

    const handleFocus = (event) => {
      if (event.target === containerRef.current) {
        setIsSubMenuOpen(true);
      }
    };

    return (
      <div
        className={classNames(classes.root, className)}
        ref={containerRef}
        onFocus={handleFocus}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        {...ContainerProps}
      >
        <MenuItem
          ref={menuItemRef}
          classes={{
            root: classes.menuItem,
          }}
          {...MenuItemProps}
        >
          {leftIcon && (
            <div className={classes.menuItemLeftContainer}>
              <div className={classes.menuItemLeftIcon}>
                {leftIcon}
              </div>
              {label}
            </div>)
          }
          {!leftIcon && label}
          {rightIcon}
        </MenuItem>
        <Menu
          // Set pointer events to 'none' to prevent the invisible Popover div
          // from capturing events for clicks and hovers
          style={{ pointerEvents: 'none' }}
          anchorEl={menuItemRef.current}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          open={open}
          autoFocus={false}
          disableAutoFocus
          disableEnforceFocus
          onClose={() => {
            setIsSubMenuOpen(false);
          }}
        >
          <div ref={menuContainerRef} style={{ pointerEvents: 'auto' }}>
            {children}
          </div>
        </Menu>
      </div>
    );
  },
);

NestedMenuItem.defaultProps = {
  className: '',
};

NestedMenuItem.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  label: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

export default withStyles(styles)(NestedMenuItem);
